import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { sanitizeTagCategories } from 'helpers'

import TabMenu from 'js/components/Navigation/TabMenu'
import TabPage from 'js/components/Navigation/TabPage'
import Spacer from 'js/components/Spacer/Spacer'
import WidgetViewPortal from 'js/page/Widget/components/components//WidgetViewPortal'
import convertObjectToWidgetSettings from 'js/page/Widget/helpers/convertObjectToWidgetSettings'
import useCustomTags from 'js/page/Widget/hooks/useCustomTags'

import CreateContent from '../CreateContent'
import { useSubtypeIsSupported } from '../helpers/supportedSubtypes'
import SettingsType from '../types/SettingsType'

import Content from './components/Content'
import General from './components/General'

interface Props {
    guid: string
    widgetGroup: object
    site: {
        guid: string
        customTagsAllowed: boolean
        tagCategories: any
        entityFilter: any
    }
    settings: SettingsType
    onSave: (value: object) => void
}

const CreateContentEdit = forwardRef(
    ({ guid, widgetGroup, site, settings, onSave }: Props, ref) => {
        const { tagCategories } = site

        const { t } = useTranslation()

        useImperativeHandle(ref, () => ({
            onSave() {
                submitForm()
            },
        }))

        const [tab, setTab] = useState('general')

        const { categoryTags, tags } = settings

        const [categoryTagsValue, setCategoryTagsValue] = useState(categoryTags)

        const {
            customTags,
            handleAddTags,
            handleRemoveTags,
            getParsedCustomTags,
        } = useCustomTags(tags)

        const [groupGuid, setGroupGuid] = useState(settings.groupGuid || null)

        const formMethods = useForm({
            defaultValues: settings,
        })
        const { handleSubmit, watch } = formMethods

        const watchValues = watch()

        const filterValues = ({
            layout,
            title,
            description,
            subtype,
            buttonSize,
            buttonAlignment,
        }) => {
            return convertObjectToWidgetSettings({
                layout,
                title,
                description,
                subtype,
                groupGuid: watchSubtypeSupportsGroup ? groupGuid : null,
                ...(layout === 'button'
                    ? {
                          buttonSize,
                          buttonAlignment,
                      }
                    : {}),
                ...(layout === 'form'
                    ? {
                          categoryTags: JSON.stringify(
                              sanitizeTagCategories(
                                  tagCategories,
                                  categoryTagsValue,
                              ),
                          ),
                          tags: getParsedCustomTags(),
                      }
                    : {}),
            })
        }

        const onSubmit = () => {
            onSave(filterValues(watchValues))
        }
        const submitForm = handleSubmit(onSubmit)

        const watchSubtypeSupportsGroup = useSubtypeIsSupported(
            'group',
            watch('subtype'),
        )

        return (
            <>
                <FormProvider {...formMethods}>
                    <Spacer spacing="small">
                        <TabMenu
                            label={t('global.settings')}
                            options={[
                                {
                                    onClick: () => setTab('general'),
                                    label: t('global.general'),
                                    isActive: tab === 'general',
                                    id: 'tab-general',
                                    ariaControls: 'tabpanel-general',
                                },
                                {
                                    onClick: () => setTab('content'),
                                    label: t('global.content'),
                                    isActive: tab === 'content',
                                    id: 'tab-content',
                                    ariaControls: 'tabpanel-content',
                                },
                            ]}
                            showBorder
                            edgePadding
                            edgeMargin
                        />

                        <TabPage
                            visible={tab === 'general'}
                            id={`tabpanel-general`}
                            ariaLabelledby={`tab-general`}
                        >
                            <General guid={guid} />
                        </TabPage>

                        <TabPage
                            visible={tab === 'content'}
                            id={`tabpanel-content`}
                            ariaLabelledby={`tab-content`}
                        >
                            <Content
                                guid={guid}
                                showGroupField={
                                    !widgetGroup && watchSubtypeSupportsGroup // Hide group field for custom types
                                }
                                site={site}
                                groupGuid={groupGuid}
                                setGroupGuid={setGroupGuid}
                                categoryTagsValue={categoryTagsValue}
                                setCategoryTagsValue={setCategoryTagsValue}
                                customTags={customTags}
                                handleAddTags={handleAddTags}
                                handleRemoveTags={handleRemoveTags}
                            />
                        </TabPage>
                    </Spacer>
                </FormProvider>

                <WidgetViewPortal
                    Component={CreateContent}
                    settings={filterValues(watchValues)}
                    guid={guid}
                    group={widgetGroup}
                />
            </>
        )
    },
)

CreateContentEdit.displayName = 'CreateContentEdit'

export default CreateContentEdit
